import { SimpleCheckList } from '@components'

import { Button, Color, SkeletonBox, Space, Text } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SubscriptionPeriod } from '@modules-deprecated/app/organization/enums/subscriptionPeriod'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { OrganizationMarket } from '../../../../../../../../types/organizationMarket'
import { useSubscriptionMetrics } from '../../../../../../../findAccountant/hooks/useSubscriptionMetrics'
import { useSubscriptionPlanPrices } from '../../../../hooks/useSubscriptionPlanPrices'
import { resolveSubscriptionPlan } from '../../../../utils/resolveSubscriptionPlan'
import { useSelectPlan } from '../../contexts/planSelectionContext'
import { useIsCurrentPlan } from '../../hooks/useIsCurrentPlan'
import { getPlanPriceSuffixTKey } from '../../utils/getPlanPriceSuffixTKey'
import * as Styled from './styles'

interface FreePlanCardProps {
  plan: SubscriptionPlan
  market?: OrganizationMarket
}

export const FreePlanCard = ({ plan, market = '' }: FreePlanCardProps): ReactElement => {
  const { t } = useTranslation()
  const { data: subscriptionMetrics, isLoading: loadingSubscriptionMetrics } = useSubscriptionMetrics()
  const { selectPlan } = useSelectPlan()
  const { isCurrentPlan } = useIsCurrentPlan(plan)
  const { yearlyPricePerMonth, isLoading: isLoadingPrice } = useSubscriptionPlanPrices(plan)

  const { content: planContent } = useMemo(() => resolveSubscriptionPlan(plan, market || undefined), [plan, market])
  const { cta = '', description = '', features = [], featuresHeader, name } = planContent

  const isCurrentPlanEligible = subscriptionMetrics?.plans.free.isEligible
  const hasExceededLimits = !loadingSubscriptionMetrics && !isCurrentPlanEligible
  const isCardDisabled = hasExceededLimits && !isCurrentPlan

  const handleUpgradePlanButtonClick = useCallback(() => {
    selectPlan(plan, SubscriptionPeriod.Yearly)
  }, [selectPlan, plan])

  return (
    <Styled.PlanCardWrapper>
      <Styled.InnerWrapper>
        <Styled.CardTop disabled={isCardDisabled}>
          <Styled.PlanHeaderWrapper>
            <Text variant="h1">{t(name)}</Text>
            <Space size="s12" horizontal />
            {/* !yearlyPricePerMonth would be incorrect here, because it can be 0 */}
            {isLoadingPrice || yearlyPricePerMonth === undefined ? (
              <SkeletonBox width={83} height={48} />
            ) : (
              <Styled.PriceContainer>
                <Text variant="display" color={Color.Lavender500} weight="regular">
                  {yearlyPricePerMonth}
                </Text>
                <Text variant="bodyLG" color={Color.Lavender500}>
                  {t(getPlanPriceSuffixTKey(market))}
                </Text>
              </Styled.PriceContainer>
            )}
          </Styled.PlanHeaderWrapper>

          {isCurrentPlan ? (
            <Button disabled variant="outlined">
              {t('subscription.plan.current')}
            </Button>
          ) : (
            <Button onClick={handleUpgradePlanButtonClick} variant="outlined" disabled={isCardDisabled}>
              {t(cta)}
            </Button>
          )}
        </Styled.CardTop>

        <Styled.CardCenter disabled={isCardDisabled}>
          <Text alignment="left">{t(description)}</Text>
          <Space size="s12" />
          {featuresHeader && (
            <Text alignment="left" variant="bodyLG" weight="bold">
              {t(featuresHeader)}
            </Text>
          )}
          <Space size="s12" />
          <Space size="s24" />
          <SimpleCheckList items={features.map((tKey) => ({ tKey }))} />
        </Styled.CardCenter>
      </Styled.InnerWrapper>
    </Styled.PlanCardWrapper>
  )
}
